import Logger from '../Logger/Logger';
import { Messages } from './types/message';
import { RequestOptions } from './types/httpTypes';
import { ErrorHandler } from './ErrorHandler';
import { NextgenApiError } from './NextgenApiError';

/**
 * Retries the http request
 * @param url URL of request
 * @param options Options for request
 * @param retries Number for retry requests
 */
const wait = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(() => resolve(), ms);
  });

const fetchToCurl = (url: string, options: RequestOptions) => {
  const curl = [`curl -X ${options.method || 'GET'}`];

  // Add headers
  if (options.headers) {
    for (const [key, value] of Object.entries(options.headers)) {
      curl.push(`-H "${key}: ${value}"`);
    }
  }

  // Add body
  if (options.body) {
    const body = typeof options.body === 'object' ? JSON.stringify(options.body) : options.body;
    curl.push(`--data '${body}'`);
  }

  // Add URL
  curl.push(`"${url}"`);

  return curl.join(' ');
};

export const rawRequest = async (url: string, options: RequestOptions): Promise<Response> => {
  const { retries, params, errorHandlers, delay = 0, retryOnStatus, customRetryFunction, ...restOption } = options;
  const loggerServiceInstance = Logger.getInstance();
  if (!url) {
    loggerServiceInstance.error(Messages.error.E001);
    throw new Error(Messages.error.E001);
  }
  let URL = url;
  if (params) {
    URL = `${url}?${new URLSearchParams(params)}`;
  }
  try {
    if (process?.env?.REQUEST_CURL_LOG) {
      loggerServiceInstance.log(`Request curl: ${fetchToCurl(URL, options)}`);
    }

    const response: Response = await fetch(URL, restOption);
    if (response.ok) {
      return response;
    }
    if (customRetryFunction) {
      return customRetryFunction(url, options, response);
    }

    if (retries && retryOnStatus?.includes(response.status)) {
      await wait(delay);
      return rawRequest(url, {
        ...options,
        retries: retries ? retries - 1 : retries,
      });
    }

    loggerServiceInstance.error(Messages.labels.L002, URL, options, response);
    const errorHandler: ErrorHandler = new ErrorHandler(errorHandlers);
    await errorHandler.handlerErrors(response);

    return response;
  } catch (err: unknown) {
    loggerServiceInstance.error(Messages.error.E004, 'Error caught', err, URL, options);

    if (err instanceof NextgenApiError) {
      throw err;
    } else {
      throw new Error(Messages.error.E004);
    }
  }
};
